import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Container } from "reactstrap";
import { useWebChatCore } from "../../../cores/conversations";
import { Conversation } from "../../../cores/conversations/interfaces/api/get-conversations";
import { PanelWembii } from "../PanelWembii";
import { useSocketCore } from "../../../cores/socket";
import { getColor, getFirstLetters } from "../../utils/stringUtils";
import { useMeCore } from "../../../cores/me";
import { useSocialChatCore } from "../../../cores/socialChat";
import Avatar from "../Avatar";
import {
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  WHATSAPP_COLOR,
  LINKED_IN_COLOR,
  LINKED_IN_BUSINESS_COLOR,
} from "../../../constants/socialColors.constant";
import { useIntl } from "react-intl";
import {
  ContactState,
  ContactStateOptions,
} from "../../../cores/contacts/interfaces/ContactState";
import { useContactCore } from "../../../cores/contacts";
import {
  faArchive,
  faTrash,
  faUndo,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Conversations } from "../../../cores/socialChat/interfaces/conversations-group.interface";
import AssigneeRow from "./assigneesRow/AssigneesRow";
import {
  WEB_CHAT_LOADING,
  INSTAGRAM_CHAT_LOADING,
  FACEBOOK_CHAT_LOADING,
  WHATSAPP_CHAT_LOADING,
  LINKEDIN_CHAT_LOADING,
  LINKEDIN_BUSINESS_CHAT_LOADING,
} from "../../../constants/loading.constant";
import { UpdateLoadingCard } from "../UpdateLoadingCard";
import { useGlobalCore } from "../../../cores/globals";
import { useFilterDate } from "../../../hooks/useFilterDate/useFilterDate.hook";
import OwnerContainer from "../contacts/OwnerContainer";
import { ContactOwner } from "../../../cores/contacts/interfaces/api/get-contact";
import { useRouterCore } from "../../../cores/router";
import { WebChatListFilter } from "./WebChatInboxToolbar";

export const ConversationList: React.FC<{
  onSelect: (arg0: string) => void;
  conversations: Conversations;
  filter: string;
  listFilter?: WebChatListFilter;
  activeConversationId?: string | null;
}> = ({
  onSelect,
  conversations,
  filter,
  listFilter = "all",
  activeConversationId = null,
}) => {
  const [fastAssignment, setFastAssignment] = useState({
    id: "",
    active: false,
  });
  const { user, company, isAdmin, isReseller, companyRole, socialSettings } =
    useMeCore();
  const { putContact } = useContactCore();
  const { isLoading } = useGlobalCore();
  const intl = useIntl();
  const { goToBinding } = useRouterCore();
  const {
    selected,
    updateConversationStatus,
    updateConversationOwners,
    getConversations,
  } = useWebChatCore();
  const {
    conversationStatus,
    selectedSocial,
    dateFilter,
    getFacebookConversations,
    getInstagramConversations,
    getWhatsappConversations,
    getLinkedinConversations,
    getLinkedinBusinessConversations,
  } = useSocialChatCore();

  const { onlineClients, subscribeToChannel, leaveFromChannel } =
    useSocketCore();
  const { states, contactStateRequest } = useContactCore();

  const [searchResults, setSearchResults] = useState<Conversation[]>([]);
  const { filterDateConversation } = useFilterDate(dateFilter);
  const [owners, setOwners] = useState<ContactOwner[]>([]);

  useEffect(() => {
    const bucket = conversations[selectedSocial] ?? {
      active: [],
      archived: [],
      inTrash: [],
    };
    const sorted = bucket[conversationStatus]
      .sort((a, b) => (b.timestamp.full < a.timestamp.full ? -1 : 1))
      .filter((conversation) =>
        conversation.participants.find((participant) =>
          participant.name.toLowerCase().includes(filter.toLowerCase())
        )
      )
      .filter(filterDateConversation);

    let results = sorted;
    if (listFilter === "unread") {
      results = sorted.filter((c) => c.unreadCount > 0);
    } else if (listFilter === "unresolved") {
      results = sorted.filter((c) => {
        const st = c.participants[0]?.state;
        if (st === undefined || st === null) return true;
        return String(st) !== String(ContactState.Connected);
      });
    }

    setSearchResults(results);
  }, [
    filter,
    conversations,
    conversationStatus,
    selectedSocial,
    dateFilter,
    listFilter,
  ]);

  const itsOnline = useCallback(
    (id: string) => onlineClients.includes(id),
    [onlineClients]
  );
  const canModify = useMemo(
    () => isAdmin || isReseller || companyRole?.owner || companyRole?.admin,
    [isAdmin, isReseller, companyRole, user]
  );
  const onAddingOwner = useCallback(
    async (user, conversation: Conversation) => {
      const mergedOwners = [...owners, user];
      setOwners(mergedOwners);
      const participant = conversation.participants[0];

      await putContact(
        company?.id!,
        participant.id,
        {
          name: conversation.participants[0].name,
          owners: mergedOwners.map((owner) => owner.id),
        },
        () => {
          getConversations(company?.id!, contactStateRequest);
          if (socialSettings?.facebook) {
            getFacebookConversations(company?.id!);
          }
          if (socialSettings?.whatsapp) {
            getWhatsappConversations(company?.id!, contactStateRequest);
          }
          if (socialSettings?.instagramMensseger) {
            getInstagramConversations(company?.id!);
          }
          if (socialSettings?.linkedIn) {
            getLinkedinConversations(company?.id!);
          }
          if (socialSettings?.linkedInBusiness) {
            getLinkedinBusinessConversations(company?.id!);
          }
          updateConversationOwners(
            conversation.id,
            conversationStatus,
            selectedSocial,
            mergedOwners,
            participant.id
          );
          setFastAssignment({
            active: !fastAssignment.active,
            id: conversation.id,
          });
        }
      );
    },
    [owners, putContact, setOwners, updateConversationOwners, company?.id]
  );

  const onRemovingOwner = useCallback(
    async (id: string, conversation: Conversation) => {
      const participant = conversation.participants[0];
      const newOwners = (participant.owners ?? []).filter(
        (owner) => owner.id !== id
      );
      setOwners(newOwners);
      await putContact(
        company?.id!,
        participant.id,
        {
          name: participant.name,
          owners: newOwners.map((owner) => owner.id) ?? [],
        },
        () => {
          getConversations(company?.id!, contactStateRequest);
          if (socialSettings?.facebook) {
            getFacebookConversations(company?.id!);
          }
          if (socialSettings?.whatsapp) {
            getWhatsappConversations(company?.id!, contactStateRequest);
          }
          if (socialSettings?.instagramMensseger) {
            getInstagramConversations(company?.id!);
          }
          if (socialSettings?.linkedIn) {
            getLinkedinConversations(company?.id!);
          }
          if (socialSettings?.linkedInBusiness) {
            getLinkedinBusinessConversations(company?.id!);
          }
          updateConversationOwners(
            conversation.id,
            conversationStatus,
            selectedSocial,
            newOwners,
            participant.id
          );
          setFastAssignment({
            active: !fastAssignment.active,
            id: conversation.id,
          });
        }
      );
    },
    [owners, company?.id, setOwners, putContact]
  );

  const handleSelectConversation = useCallback(
    async (conversation) => {
      if (selected && selected.type === "web") leaveFromChannel(selected.room!);
      onSelect(conversation);
    },
    [onSelect, selected, leaveFromChannel]
  );
  useEffect(() => {
    if (user && user.id && selected && selected.room) {
      subscribeToChannel(user?.id!, selected.id, selected.room, company?.id!);
    }
  }, [selected, user, subscribeToChannel, company]);

  const getParticipantNames = useCallback(
    (conversation: Conversation) =>
      conversation.participants
        .map((participant) => participant.name)
        .join(", "),
    []
  );

  const handleUpdateConversationStatus = useCallback(
    async (conversation: Conversation) => {
      await updateConversationStatus(
        {
          companyId: Number(company?.id),
          conversationId: conversation.id,
          socialName: conversation.type,
          status: "inTrash",
        },
        conversationStatus
      );
    },

    [company?.id, updateConversationStatus, conversationStatus]
  );

  const handleConversationSelection = useCallback(
    async (conversation: Conversation) => {
      await updateConversationStatus(
        {
          companyId: Number(company?.id),
          conversationId: conversation.id,
          socialName: conversation.type,
          status: conversationStatus === "archived" ? "active" : "archived",
        },
        conversationStatus
      );
    },
    [company?.id, conversationStatus, updateConversationStatus]
  );

  const statesValues = useMemo(
    () => [
      ...ContactStateOptions.map((state) => ({
        value: state.value,
        label: intl.formatMessage({ id: state.label }),
      })),
      ...states.map((state) => ({ value: state.id, label: state.name })),
    ],
    [states, intl]
  );

  const getStateValue = useCallback(
    (state) => {
      return statesValues.find((stt) => stt.value === state)?.label;
    },
    [statesValues]
  );

  const totalAllConversations = useMemo(() => {
    const all = conversations.all;
    return (
      all.active.length + all.archived.length + all.inTrash.length
    );
  }, [conversations]);

  const getLabelSnippet = ({ snippet, messageType }: Conversation) => {
    if (messageType) {
      switch (messageType) {
        case "text":
          return snippet;
        case "audio":
          return intl.formatMessage({ id: "app.chat.sentAudio" });
        case "video":
          return intl.formatMessage({ id: "app.chat.sentVideo" });
        case "image":
          return intl.formatMessage({ id: "app.chat.sentImage" });
        case "document":
          return intl.formatMessage({ id: "app.chat.sentAttachment" });
        default:
          return snippet;
      }
    }
    return snippet;
  };
  return (
    <Container className="p-0 px-2 pb-2 position-relative w-100 flex-grow-1 conversation-list-scroll">
      {isLoading(WEB_CHAT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={WEB_CHAT_LOADING}
          text={intl.formatMessage({ id: "app.chat.UpdatingWebChats" })}
        />
      ) : isLoading(INSTAGRAM_CHAT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={INSTAGRAM_CHAT_LOADING}
          text={intl.formatMessage({ id: "app.chat.UpdatingInstagramChats" })}
        />
      ) : isLoading(FACEBOOK_CHAT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={FACEBOOK_CHAT_LOADING}
          text={intl.formatMessage({ id: "app.chat.UpdatingFacebookChats" })}
        />
      ) : isLoading(WHATSAPP_CHAT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={WHATSAPP_CHAT_LOADING}
          text={intl.formatMessage({ id: "app.chat.UpdatingWhatsappChats" })}
        />
      ) : isLoading(LINKEDIN_CHAT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={LINKEDIN_CHAT_LOADING}
          text={intl.formatMessage({ id: "app.chat.UpdatingLinkedinChats" })}
        />
      ) : isLoading(LINKEDIN_BUSINESS_CHAT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={LINKEDIN_BUSINESS_CHAT_LOADING}
          text={intl.formatMessage({
            id: "app.chat.UpdatingLinkedinBusinessChats",
          })}
        />
      ) : (
        <>
          {searchResults.length === 0 && (
            <div className="px-3 py-4 text-center">
              {totalAllConversations === 0 ? (
                <>
                  <p className="text-muted small mb-3">
                    {intl.formatMessage({ id: "app.chat.listEmpty.title" })}
                  </p>
                  <Button
                    color="primary"
                    size="sm"
                    className="rounded-pill"
                    onClick={goToBinding}
                  >
                    {intl.formatMessage({ id: "app.chat.listEmpty.cta" })}
                  </Button>
                </>
              ) : (
                <p className="text-muted small mb-0">
                  {intl.formatMessage({ id: "app.chat.searchNoResults" })}
                </p>
              )}
            </div>
          )}
          {searchResults.map((conversation) => (
            <PanelWembii
              key={conversation.id}
              className={cn(
                "conversation-list-item pointer my-1",
                {
                  "conversation-list-item--active":
                    activeConversationId != null &&
                    String(conversation.id) ===
                      String(activeConversationId),
                }
              )}
            >
              <div className="conversation-list-item__row d-flex align-items-stretch p-2">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectConversation(conversation)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      handleSelectConversation(conversation);
                  }}
                  className="conversation-list-item__main d-flex flex-grow-1 align-items-start"
                >
                  <div className="conversation-list-item__avatar-col flex-shrink-0 position-relative">
                    <Avatar
                      className="border conversation-list-item__avatar"
                      label={getFirstLetters(
                        conversation.participants[0].name
                      )}
                      image={conversation.participants[0].avatarUrl}
                      backgroundColor={getColor(parseInt(conversation.id))}
                      color="white"
                      width="48px"
                      height="48px"
                    />
                    <span className="conversation-list-item__channel-dot">
                      {conversation.type === "facebook" && (
                        <FontAwesomeIcon
                          icon={["fab", "facebook"]}
                          style={{ color: FACEBOOK_COLOR }}
                        />
                      )}
                      {conversation.type === "instagram" && (
                        <FontAwesomeIcon
                          icon={["fab", "instagram"]}
                          style={{ color: INSTAGRAM_COLOR }}
                        />
                      )}
                      {conversation.type === "whatsapp" && (
                        <FontAwesomeIcon
                          icon={["fab", "whatsapp"]}
                          style={{ color: WHATSAPP_COLOR }}
                        />
                      )}
                      {conversation.type === "web" && (
                        <span
                          className={cn(
                            "rounded-circle d-inline-block conversation-list-item__web-dot",
                            {
                              "bg-success": itsOnline(
                                conversation.participants[0].id
                              ),
                              "bg-secondary": !itsOnline(
                                conversation.participants[0].id
                              ),
                            }
                          )}
                        />
                      )}
                      {conversation.type === "linkedin" && (
                        <FontAwesomeIcon
                          icon={["fab", "linkedin"]}
                          style={{ color: LINKED_IN_COLOR }}
                        />
                      )}
                      {conversation.type === "linkedinBusiness" && (
                        <FontAwesomeIcon
                          icon={["fab", "linkedin"]}
                          style={{ color: LINKED_IN_BUSINESS_COLOR }}
                        />
                      )}
                    </span>
                    <AssigneeRow
                      avatars={conversation.participants[0].owners ?? []}
                    />
                  </div>
                  <div className="conversation-list-item__text flex-grow-1">
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="conversation-list-item__name-wrap flex-grow-1 pr-2">
                        <div className="d-flex align-items-center flex-wrap">
                          <span className="conversation-list-item__name font-weight-bold text-dark text-truncate mr-1">
                            {getParticipantNames(conversation)}
                          </span>
                          {conversation.participants[0].state !==
                            undefined && (
                            <Badge
                              color="light"
                              className="text-secondary border conversation-list-item__state-badge"
                              pill
                            >
                              {getStateValue(
                                conversation.participants[0].state
                              )}
                            </Badge>
                          )}
                          {conversation.unreadCount > 0 && (
                            <Badge
                              color="danger"
                              className="text-white px-2"
                              pill
                            >
                              {conversation.unreadCount}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <time
                        className="conversation-list-item__time flex-shrink-0 text-muted"
                        dateTime={conversation.timestamp.full}
                      >
                        {conversation.timestamp.date}
                      </time>
                    </div>
                    <p className="conversation-list-item__snippet text-muted small mb-0 mt-1 text-truncate">
                      {conversation.type === "whatsapp"
                        ? getLabelSnippet(conversation)
                        : conversation.snippet}
                    </p>
                  </div>
                </div>

                <div
                  className="conversation-list-item__actions flex-shrink-0 d-flex flex-column align-items-center position-relative"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {fastAssignment.active &&
                    fastAssignment.id === conversation.id && (
                      <div className="conversation-list-item__assign-popover bg-white shadow-sm p-2 rounded border">
                        <small className="text-nowrap d-block mb-1">
                          Asignación rápida
                        </small>
                        <OwnerContainer
                          contacts={owners}
                          canModify={canModify}
                          onAdding={(data) =>
                            onAddingOwner(data, conversation)
                          }
                          onRemoving={(data) =>
                            onRemovingOwner(data, conversation)
                          }
                        />
                      </div>
                    )}
                  {(conversation.participants[0].socials !== undefined ||
                    conversation.type === "web") && (
                    <button
                      type="button"
                      title="Asignar"
                      onClick={() => {
                        setFastAssignment({
                          active: !fastAssignment.active,
                          id: conversation.id,
                        });
                        setOwners(conversation.participants[0]!.owners ?? []);
                      }}
                      className="btn btn-link conversation-list-item__action-btn p-1"
                    >
                      <FontAwesomeIcon
                        icon={faUserPlus}
                        size="sm"
                        className="text-secondary"
                      />
                    </button>
                  )}
                  <button
                    type="button"
                    title={
                      conversationStatus === "archived"
                        ? "Restaurar"
                        : "Archivar"
                    }
                    onClick={() => handleConversationSelection(conversation)}
                    className="btn btn-link conversation-list-item__action-btn p-1"
                  >
                    {conversationStatus === "archived" ? (
                      <FontAwesomeIcon
                        icon={faUndo}
                        size="sm"
                        className="text-secondary"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faArchive}
                        size="sm"
                        className="text-secondary"
                      />
                    )}
                  </button>
                  {(conversationStatus === "archived" ||
                    conversationStatus === "active") && (
                    <button
                      type="button"
                      title="Eliminar"
                      onClick={() =>
                        handleUpdateConversationStatus(conversation)
                      }
                      className="btn btn-link conversation-list-item__action-btn p-1"
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        size="sm"
                        className="text-danger"
                      />
                    </button>
                  )}
                </div>
              </div>
            </PanelWembii>
          ))}
        </>
      )}
    </Container>
  );
};
